// /calendar — the ops calendar (B1–B8).
//
// Everyone sees it on the real reads (calendar.source.ts). Real data is
// read-only for now: Assign opens Dispatch and Edit shift opens Schedules,
// because the calendar has no endpoint of its own to save either yet.
//
// ?demo=1 (and, in production, a Super Admin) runs it on simulated data
// (calendar.demo.ts), where Assign / Edit shift change only an in-memory list
// of edits with Undo (calendar.mock-edits.ts).

import { Button } from "@bednbody/ui/components/button";
import { PageLoading } from "@bednbody/ui/components/page-loading";
import { useMemo, useRef, useState } from "react";
import { useAuth } from "@/lib/auth";
import { CalendarBoardPage } from "./CalendarBoardPage";
import { type CalendarDemo, useCalendarDemo } from "./calendar.demo";
import { applyMockEdits, type MockEdit, withoutEdit } from "./calendar.mock-edits";
import { useRealCalendarSource } from "./calendar.source";
import { useCalendarRealtimeInvalidation } from "./calendar.hooks";
import type { CalendarActions } from "./calendar-board";
import { localDateIso } from "./calendar-model";
import { isHubScoped } from "@/lib/jwt-claims";

export function CalendarRoute() {
  const { claims } = useAuth();
  const demo = useCalendarDemo(claims?.role === "superadmin");
  return demo ? <DemoCalendar demo={demo} /> : <RealCalendar />;
}

function RealCalendar() {
  const { claims } = useAuth();
  const today = useMemo(() => localDateIso(), []);
  const [day, setDay] = useState(today);
  const real = useRealCalendarSource(day);
  // A published schedule, a booking or a trip changing refreshes the week.
  useCalendarRealtimeInvalidation({
    role: claims?.role ?? "admin",
    hubId: claims && isHubScoped(claims.role) ? claims.hub_id : null,
  });

  if (real.error && !real.source) {
    return (
      <div className="grid flex-1 place-items-center">
        <div className="space-y-3 text-center">
          <p className="text-sm text-muted-foreground">The calendar could not load.</p>
          <Button onClick={real.refetch} variant="outline">
            Try again
          </Button>
        </div>
      </div>
    );
  }
  if (!real.source) return <PageLoading className="min-h-72 flex-1" label="Loading calendar..." />;
  return (
    <CalendarBoardPage
      actions={null}
      nowMs={real.nowMs}
      onDayChange={setDay}
      simulated={false}
      source={real.source}
      today={today}
    />
  );
}

function DemoCalendar({ demo }: { demo: CalendarDemo }) {
  const [edits, setEdits] = useState<MockEdit[]>([]);
  const nextId = useRef(0);

  const source = useMemo(() => applyMockEdits(demo.source, edits), [demo, edits]);
  const actions = useMemo<CalendarActions>(() => {
    const add = (make: (id: string) => MockEdit) => {
      const id = String(++nextId.current);
      setEdits((current) => [...current, make(id)]);
      return () => setEdits((current) => withoutEdit(current, id));
    };
    return {
      assign: (bookingId, therapistId) =>
        add((id) => ({ id, kind: "assign", bookingId, therapistId })),
      saveShift: (staffId, day, shift) => add((id) => ({ id, kind: "shift", staffId, day, shift })),
    };
  }, []);

  return (
    <CalendarBoardPage
      actions={actions}
      nowMs={demo.nowMs}
      simulated
      source={source}
      today={demo.today}
    />
  );
}
